import { trackEvent } from '@msc-b2c/shared-utils';

export class EditorialSearch {
  private readonly editorialSearch: HTMLElement;

  constructor(editorialSearch: HTMLElement) {
    this.editorialSearch = editorialSearch;
  }
  init() {
    const searchInput = this.editorialSearch.querySelector('.hidden-searchbar-input');
    const searchInputArialLabel = this.editorialSearch.querySelector('.ais-SearchBox-input');
    const searchCTALabel = searchInput?.getAttribute('ctalabel')
      ? searchInput?.getAttribute('ctalabel')
      : searchInputArialLabel?.getAttribute('aria-label');
    const searchButton = this.editorialSearch.querySelector('.ais-SearchBox-submit');
    trackAnalytics(this.editorialSearch);
    if (searchButton) {
      searchButton.innerHTML = `<span>${searchCTALabel}</span>`;
      searchButton.addEventListener('click', () => {
        const newQuery = (searchInputArialLabel as HTMLInputElement)?.value?.trim();
        if (newQuery) {
          const url = new URL(window.location.href);
          url.searchParams.set('query', newQuery);
          window.history.pushState({}, '', url.toString());
        }
        const e = new KeyboardEvent('keypress', {
          bubbles: true,
          cancelable: true,
          key: 'Enter',
          code: 'Enter',
          which: 13,
          keyCode: 13,
        });
        searchInputArialLabel?.dispatchEvent(e);
      });

      /* Simulate click on the button - start */
      searchInputArialLabel?.addEventListener('keydown', (event) => {
        clickButton(event, searchButton);
      });

      searchButton.addEventListener('keydown', (event) => {
        clickButton(event, searchButton);
      });
      /* Simulate click on the button - end */
    }
  }
}

function clickButton(event: Event, searchButton: Element) {
  const eventKey = event as KeyboardEvent;
  if (eventKey.code === 'Enter') {
    event.preventDefault();
    searchButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  }
}

const observers: MutationObserver[] = [];

function getInstantSearchQuery(container: ParentNode): string {
  const searchInput = container.querySelector<HTMLInputElement>('.ais-SearchBox-input');
  const inputValue = searchInput?.value?.trim();
  if (inputValue) {
    return inputValue;
  }

  return new URLSearchParams(window.location.search).get('query')?.trim() ?? '';
}

function parseLocalizedNumber(text: string): number | null {
  const match = text.match(/[\d][\d.,\s]*/);
  if (!match) {
    return null;
  }

  const normalized = match[0].replace(/\D/g, '');
  if (!normalized) {
    return null;
  }

  const count = Number.parseInt(normalized, 10);
  return Number.isNaN(count) ? null : count;
}

function getInstantSearchResultsCount(container: ParentNode): number {
  const heading = container.querySelector('#aggregatedSearchResultsHeading');

  const nbHitsElement = heading?.querySelector('.nbHits');
  if (nbHitsElement?.textContent) {
    const count = parseLocalizedNumber(nbHitsElement.textContent);
    if (count !== null) {
      return count;
    }
  }

  const statsText = container.querySelector('.ais-Stats-text')?.textContent;
  if (statsText) {
    const count = parseLocalizedNumber(statsText);
    if (count !== null) {
      return count;
    }
  }

  if (heading?.textContent) {
    const count = parseLocalizedNumber(heading.textContent);
    if (count !== null) {
      return count;
    }
  }

  return 0;
}

function trackAnalytics(container: HTMLElement): void {
  let debounceTimeout: NodeJS.Timeout | null = null;
  let previousSignature: string | null = null;

  const mutationCallback: MutationCallback = () => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    debounceTimeout = setTimeout(() => {
      const searchTerm = getInstantSearchQuery(container);
      if (!searchTerm) {
        return;
      }

      const resultsCount = getInstantSearchResultsCount(container);
      const signature = `${searchTerm}|${resultsCount}`;

      if (signature === previousSignature) {
        return;
      }

      previousSignature = signature;

      trackEvent('internal_search', {
        int_search_term: searchTerm,
        int_search_results: String(resultsCount),
      });
    }, 600);
  };

  const observer = new MutationObserver(mutationCallback);
  observer.observe(container, { childList: true, subtree: true, characterData: true });
  observers.push(observer);
}

function onDocumentReady(): void {
  document.querySelectorAll('.algoliainstantsearch').forEach((node) => {
    const editorialSearch = new EditorialSearch(node as HTMLElement);
    editorialSearch.init();
  });
}
if (document.readyState !== 'loading') {
  onDocumentReady();
} else {
  document.addEventListener('DOMContentLoaded', onDocumentReady);
}
